/* eslint-disable array-callback-return */
import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBuilding,
  faSignOutAlt,
  faArrowLeft,
  faInfoCircle,
  faChevronDown,
  faChevronUp,
  faBars,
} from '@fortawesome/free-solid-svg-icons'

// Importações do Material-UI
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  CssBaseline,
  Stack,
  Collapse,
  Slide,
} from '@mui/material'

import { useAuth } from '../hooks/useAuth'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOrgan } from '../hooks/useOrgan'
import { usePermissions } from '../hooks/usePermissions'
import useLoader from '../hooks/useLoader'
import useBaseService from '../hooks/services/useBaseService'
import { entities } from '../constants/entities'
import { useApplication } from '../hooks/useApplication'
import ApplicationEnum from '../enums/application-enum'
import { sectionsAlmoxarifado } from '../constants/routes/almoxarifado/routes-sections'
import {
  sectionAluguelAdmin,
  sectionsAluguel,
} from '../constants/routes/aluguel/routes-sections'
import toTitleCase from '../utils/toTitleCase'
import { sectionsManutenção } from '../constants/routes/manutenção/routes-sections'
import ReportProgressBar from '../components/ReportProgressBar'
import useQueryWithLoader from '../hooks/useQueryWithLoader'
import { sectionsFaturamento } from '../constants/routes/faturamento/routes-sections'

const drawerWidth = 200
const fixedAppBarHeight = 68

// As cores primárias continuam as mesmas, pois são para as organizações, não para o tema.
export const organizationPrimaryColors = {
  ALUCOM: '#BF2626', // Vermelho
  MOREIA: '#FF8C00', // Laranja
  IP: '#639AC4', // Azul claro
  'AS SISTEMAS': '#2C3E50', // Azul escuro
  FATURAMENTO: '#2D2D2D',
  ADMIN: '#0F50AC',
}

const PermanentSidebarMUI = ({ children }) => {
  const { user, logout } = useAuth()
  const { canAccess, UserHasRole, userRoles } = usePermissions()
  const { showLoader, hideLoader } = useLoader()
  const navigate = useNavigate()
  const location = useLocation()
  const { getByColumn: fetchById } = useBaseService(navigate)
  const { selectedApplication } = useApplication()
  const { selectedOrgan, clearOrganSelection } = useOrgan()
  const [menuSections, setMenuSections] = useState([])

  const [openSections, setOpenSections] = useState({})

  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  let headerTextColor = '#FFFFFF'
  if (selectedOrgan?.name) {
    switch (selectedOrgan.name.toUpperCase()) {
      case 'IP':
        headerTextColor = '#233E76'
        break
      case 'MOREIA':
        headerTextColor = '#FFFFFF'
        break
      case 'ALUCOM':
        headerTextColor = '#FFFFFF'
        break
      case 'AS SISTEMAS':
        headerTextColor = '#FFFFFF'
        break
      default:
        headerTextColor = '#FFFFFF'
    }
  }

  const headerBackgroundColor = primaryColor
  // Cores fixas para o tema claro
  const sidebarContentBackgroundColor = '#FFFFFF'
  const sidebarTextSelectedColor = primaryColor // Mantém a cor da organização para o texto selecionado

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Estado para armazenar o caminho da rota do item de menu atualmente ativo (o mais específico)
  const [activeItemRoute, setActiveItemRoute] = useState('')

  // UseEffect para calcular o item de menu ativo e gerenciar as seções abertas
  useEffect(() => {
    let sections = []

    if (selectedOrgan && selectedOrgan?.id === 'admin') {
      sections = sectionAluguelAdmin.map((section) => {
        if (section.items) {
          return {
            ...section,
              items: section.items.filter((item) =>
                canAccess(item.requiredPermission) ||
                (item.requiredPermission === 'Listar logs' &&
                  UserHasRole(['Admin', 'Super Admin'], userRoles))
            ),
          }
        }
        return {
          ...section,
          _canAccess: canAccess(section.requiredPermission),
        }
      })
    } else if (
      selectedApplication?.id === ApplicationEnum.ALUGUEL &&
      selectedOrgan &&
      selectedOrgan?.id !== 'admin'
    ) {
      sections = sectionsAluguel(user)
        .filter((section) => section.title !== 'Usuário')
        .map((section) => {
        if (section.items) {
          return {
            ...section,
            items: section.items.filter((item) =>
              canAccess(item.requiredPermission)
            ),
          }
        }
        return {
          ...section,
          _canAccess: canAccess(section.requiredPermission),
        }
        })
    }

    if (
      selectedApplication?.id === ApplicationEnum.ALMOXARIFADO &&
      selectedOrgan &&
      selectedOrgan?.id !== 'admin'
    ) {
      sections = sectionsAlmoxarifado.map((section) => {
        if (section.items) {
          return {
            ...section,
            items: section.items.filter((item) =>
              canAccess(item.requiredPermission)
            ),
          }
        } else {
          return {
            ...section,
            _canAccess: canAccess(section.requiredPermission),
          }
        }
      })
    }

    if (
      selectedApplication?.id === ApplicationEnum.MANUTENÇÃO &&
      selectedOrgan &&
      selectedOrgan?.id !== 'admin'
    ) {
      sections = sectionsManutenção.map((section) => {
        if (section.items) {
          return {
            ...section,
            _canAccess: section.requiredPermission
              ? canAccess(section.requiredPermission)
              : true, // ✅ Verifica permissão da seção
            items: section.items.filter((item) =>
              canAccess(item.requiredPermission)
            ),
          }
        } else {
          return {
            ...section,
            _canAccess: canAccess(section.requiredPermission),
          }
        }
      })
    }

    if (
      selectedApplication?.id === ApplicationEnum.FATURAMENTO &&
      selectedOrgan?.id === 'faturamento'
    ) {
      sections = sectionsFaturamento.map((section) => {
        if (section.items) {
          return {
            ...section,
            items: section.items.filter((item) =>
              canAccess(item.requiredPermission)
            ),
          }
        } else {
          return {
            ...section,
            _canAccess: canAccess(section.requiredPermission),
          }
        }
      })
    }

    const filteredSections = sections.filter((section) => {
      if (section.items) {
        // Se tem sub-itens: verifica a permissão da seção (se existir) E se ainda tem itens válidos
        const temPermissao = section.requiredPermission
          ? canAccess(section.requiredPermission)
          : true
        return temPermissao && section.items.length > 0
      }
      // Se é um item direto (sem sub-itens): verifica a permissão ou se tem uma rota definida
      const temPermissao = section.requiredPermission
        ? canAccess(section.requiredPermission)
        : true
      return temPermissao || section.to
    })

    setMenuSections(filteredSections)

    // Lógica para determinar o item de menu mais específico atualmente ativo
    let bestMatchPath = ''
    let longestMatchLength = 0

    filteredSections.forEach((section) => {
      if (section.items) {
        section.items.forEach((item) => {
          const cleanItemPath = item.to.split('/:')[0] // Remove parâmetros como ':id'
          if (location.pathname.startsWith(cleanItemPath)) {
            if (cleanItemPath.length > longestMatchLength) {
              bestMatchPath = cleanItemPath
              longestMatchLength = cleanItemPath.length
            } else if (
              cleanItemPath.length === longestMatchLength &&
              location.pathname === cleanItemPath
            ) {
              // Preferir correspondência exata se o comprimento for o mesmo
              bestMatchPath = cleanItemPath
            }
          }
        })
      } else {
        // Para seções que são itens diretos (sem sub-itens, como Dashboard)
        const cleanSectionPath = section.to.split('/:')[0]
        if (location.pathname.startsWith(cleanSectionPath)) {
          if (cleanSectionPath.length > longestMatchLength) {
            bestMatchPath = cleanSectionPath
            longestMatchLength = cleanSectionPath.length
          } else if (
            cleanSectionPath.length === longestMatchLength &&
            location.pathname === cleanSectionPath
          ) {
            bestMatchPath = cleanSectionPath
          }
        }
      }
    })
    setActiveItemRoute(bestMatchPath)

    // Lógica para abrir/colapsar as seções (mais "generosa" que a seleção do item)
    const newOpenState = {}
    let activeSectionFoundAndOpened = false

    filteredSections.forEach((section) => {
      newOpenState[section.title] = false // Padrão é fechado

      if (section.items) {
        // Abre a seção se a URL atual começar com o caminho limpo de qualquer sub-item
        const isActiveSubItem = section.items.some((item) =>
          location.pathname.startsWith(item.to.split('/:')[0])
        )
        if (section.opened === true || isActiveSubItem) {
          newOpenState[section.title] = true
          if (isActiveSubItem) {
            activeSectionFoundAndOpened = true
          }
        }
      } else {
        // Para seções diretas, abre se a URL atual começar com o caminho limpo da seção
        if (location.pathname.startsWith(section.to.split('/:')[0])) {
          activeSectionFoundAndOpened = true
          newOpenState[section.title] = true
        }
      }
    })

    // Lógica para "Sem Permissões"
    if (!activeSectionFoundAndOpened && filteredSections.length === 0) {
      setMenuSections([
        {
          title: 'Sem Permissões',
          items: [
            { name: 'Nenhum item disponível', icon: faInfoCircle, to: '#' },
          ],
        },
      ])
      newOpenState['Sem Permissões'] = true
    }

    setOpenSections(newOpenState)
  }, [
    selectedOrgan,
    canAccess,
    UserHasRole,
    userRoles,
    selectedApplication?.id,
    user,
    location.pathname,
  ])

  const { data: userData, isLoading } = useQueryWithLoader({
    queryKey: ['user', user?.id],
    queryFn: async () => {
      const response = await fetchById(entities.users.getById(user?.id))
      return response.result
    },
    enabled: !!user?.id,
    select: (data) => ({
      name: data?.name,
      email: data?.email,
    }),
  })

  useEffect(() => {
    if (isLoading) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [hideLoader, isLoading, showLoader])

  // Garantindo que a classe 'dark-theme' seja removida (se por acaso estiver no localStorage)
  // ou que o tema padrão seja o claro. Isso pode ser feito uma vez na raiz do app ou aqui.
  useEffect(() => {
    document.body.classList.remove('dark-theme')
    localStorage.removeItem('selected-theme') // Limpa qualquer resíduo
    localStorage.removeItem('selected-icon') // Limpa qualquer resíduo
  }, []) // Executa uma vez na montagem

  const handleNavigation = (path) => {
    navigate(path)
  }

  const handleToggleSection = (sectionTitle) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }))
  }

  const [openSidebar, setOpenSidebar] = useState(() => {
    const storedStatus = localStorage.getItem('sidebarStatus')
    return storedStatus !== null ? JSON.parse(storedStatus) : true
  })

  const toggleSidebar = () => {
    setOpenSidebar((prev) => {
      const newStatus = !prev
      localStorage.setItem('sidebarStatus', JSON.stringify(newStatus))
      return newStatus
    })
  }

  const closeSidebar = () => {
    setOpenSidebar(false)
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100vw' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: openSidebar ? `calc(100% - ${drawerWidth}px)` : '100%', // ajusta largura
          ml: openSidebar ? `${drawerWidth}px` : 0, // ajusta deslocamento
          zIndex: 1201,
          backgroundColor: headerBackgroundColor,
          color: headerTextColor,
          boxShadow: 'none',
          borderBottom: 'none',
          height: `${fixedAppBarHeight}px`,
          transition: openSidebar ? 'all 0.2s ease' : '', // animação suave
        }}>
        <Toolbar sx={{ height: '100%', gap: 2 }}>
          {/* Botão para abrir/fechar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '4px',
              transition: 'background-color 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
            onClick={toggleSidebar}>
            <FontAwesomeIcon
              icon={faBars}
              size="lg"
              className="hover"
              style={{
                cursor: 'pointer',
              }}
            />
          </Box>

          <Typography
            variant="h5"
            noWrap
            component="div"
            onClick={() => window.open('/dashboard', '_blank')}
            sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '8px' }} />
            {selectedOrgan?.name || 'ADI'} - Sistema de{' '}
            {toTitleCase(selectedApplication?.name)}
          </Typography>

          <ReportProgressBar />
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        open={openSidebar}
        onClose={closeSidebar}
        sx={{
          width: openSidebar ? drawerWidth : 0,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: openSidebar ? drawerWidth : 0,
            boxSizing: 'border-box',
            backgroundColor: 'rgba(255, 255, 255, 0)',
            color: 'text.primary',
            borderRight: 'none',
            boxShadow: 'none',
            transition: 'all 0.5s ease',
            overflowX: 'hidden',
          },
        }}>
        <Box
          sx={{
            backgroundColor: headerBackgroundColor,
            p: 2,
            height: `${fixedAppBarHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Stack
            direction="column"
            alignItems="flex-start"
            spacing={0.5}
            sx={{
              width: '100%',
              px: 1,
            }}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                color: `${headerTextColor} !important`,
                fontWeight: 'bold',
              }}>
              {userData?.name}
            </Typography>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{ color: `${headerTextColor} !important` }}>
              {userData?.email}
            </Typography>
          </Stack>
        </Box>
        <Slide direction="right" in={openSidebar} timeout={400}>
          <Box
            sx={{
              overflowX: 'hidden',
              overflowY: 'auto',
              p: 1,
              backgroundColor: sidebarContentBackgroundColor,
              transition: 'all 0.2s ease',
              flexGrow: 1,
            }}>
            <List disablePadding>
              {menuSections.map((section, sectionIndex) => (
                <Box key={sectionIndex} sx={{ mb: 1 }}>
                  {section.items ? (
                    <>
                      <ListItemButton
                        onClick={() => handleToggleSection(section.title)}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                          justifyContent: 'space-between',
                          px: 1,
                          py: 1,
                        }}>
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 3 }}>
                          <FontAwesomeIcon
                            icon={section.icon}
                            style={{
                              color: 'text.primary',
                              fontSize: '0.8rem',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                              }}>
                              {section.title}
                            </Typography>
                          }
                        />
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          <FontAwesomeIcon
                            icon={
                              openSections[section.title]
                                ? faChevronUp
                                : faChevronDown
                            }
                            style={{
                              color: 'text.secondary',
                              fontSize: '0.6rem',
                            }}
                          />
                        </ListItemIcon>
                      </ListItemButton>

                      <Collapse
                        in={openSections[section.title]}
                        timeout="auto"
                        unmountOnExit>
                        <List disablePadding sx={{ pl: 1 }}>
                          {section.items.map((item, itemIndex) => {
                            const cleanItemPath = item.to.split('/:')[0]
                            const isSelected = activeItemRoute === cleanItemPath

                            return (
                              <ListItem key={itemIndex} disablePadding>
                                <ListItemButton
                                  onClick={() => handleNavigation(item.to)}
                                  selected={isSelected}
                                  sx={{
                                    borderRadius: 1,
                                    '&.Mui-selected': {
                                      backgroundColor: '#e0e0e0',
                                      color: sidebarTextSelectedColor,
                                      '& .MuiListItemIcon-root': {
                                        color: sidebarTextSelectedColor,
                                      },
                                    },
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5',
                                    },
                                  }}>
                                  <ListItemIcon
                                    sx={{ minWidth: 'auto', mr: 2 }}>
                                    <FontAwesomeIcon
                                      icon={item.icon}
                                      style={{
                                        color: 'inherit',
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          textTransform: 'capitalize',
                                        }}>
                                        {item.name}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            )
                          })}
                        </List>
                      </Collapse>
                    </>
                  ) : (
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(section.to)}
                        selected={activeItemRoute === section.to.split('/:')[0]}
                        sx={{
                          borderRadius: 1,
                          '&.Mui-selected': {
                            backgroundColor: '#e0e0e0',
                            color: sidebarTextSelectedColor,
                            '& .MuiListItemIcon-root': {
                              color: sidebarTextSelectedColor,
                            },
                          },
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                          px: 1,
                          py: 1,
                        }}>
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 3 }}>
                          <FontAwesomeIcon
                            icon={section.icon}
                            style={{ color: 'inherit', fontSize: '0.8rem' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                              }}>
                              {section.title}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  )}
                </Box>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={clearOrganSelection}
                  sx={{ borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 3 }}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: 'uppercase',
                          fontWeight: 'bold',
                        }}>
                        Voltar
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 3 }}>
                    <FontAwesomeIcon color="#D32F2F" icon={faSignOutAlt} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        color="error"
                        sx={{
                          textTransform: 'uppercase',
                          fontWeight: 'bold',
                        }}>
                        Desconectar
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Slide>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          boxSizing: 'border-box',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box sx={{ height: `${fixedAppBarHeight}px`, flexShrink: 0 }} />

        <Box
          sx={{
            flexGrow: 1,
            p: 1,
            overflowY: 'auto',
            width: '100%',
            boxSizing: 'border-box',
          }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default PermanentSidebarMUI
